import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../../config/supabase';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiFolderPlus, FiFolder, FiEdit2, FiTrash2, FiCheck, FiX, FiChevronRight } = FiIcons;

const FolderList = ({ user, onFolderSelect, selectedFolderId, onFolderUpdated }) => {
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newFolderName, setNewFolderName] = useState('');
  const [isAddingFolder, setIsAddingFolder] = useState(false);
  const [editingFolder, setEditingFolder] = useState(null);
  const [editName, setEditName] = useState('');
  const [error, setError] = useState('');

  // Load folders on component mount
  useEffect(() => {
    loadFolders();
  }, [user]);

  const loadFolders = async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('search_folders')
        .select('*')
        .eq('user_id', user.id)
        .order('name');
        
      if (error) throw error;
      
      setFolders(data || []);
    } catch (error) {
      console.error('Error loading folders:', error);
      setError('Failed to load folders');
    } finally {
      setLoading(false);
    }
  };

  const handleAddFolder = async () => {
    if (!newFolderName.trim()) {
      setError('Folder name cannot be empty');
      return;
    }

    try {
      const { data, error } = await supabase
        .from('search_folders')
        .insert([
          { user_id: user.id, name: newFolderName.trim() }
        ])
        .select();
        
      if (error) throw error;
      
      setFolders([...folders, data[0]]);
      setNewFolderName('');
      setIsAddingFolder(false);
      setError('');
      
      if (onFolderUpdated) {
        onFolderUpdated();
      }
    } catch (error) {
      console.error('Error adding folder:', error);
      setError('Failed to add folder');
    }
  };

  const handleEditFolder = (folder) => {
    setEditingFolder(folder.id);
    setEditName(folder.name);
  };

  const handleUpdateFolder = async () => {
    if (!editName.trim()) {
      setError('Folder name cannot be empty');
      return;
    }

    try {
      const { error } = await supabase
        .from('search_folders')
        .update({ name: editName.trim(), updated_at: new Date().toISOString() })
        .eq('id', editingFolder);
        
      if (error) throw error;
      
      // Update local state
      setFolders(folders.map(folder => 
        folder.id === editingFolder 
          ? { ...folder, name: editName.trim() } 
          : folder
      ));
      
      setEditingFolder(null);
      setError('');
      
      if (onFolderUpdated) {
        onFolderUpdated();
      }
    } catch (error) {
      console.error('Error updating folder:', error);
      setError('Failed to update folder');
    }
  };

  const handleDeleteFolder = async (folderId) => {
    if (window.confirm('Are you sure you want to delete this folder? All search assignments to this folder will be removed.')) {
      try {
        const { error } = await supabase
          .from('search_folders')
          .delete()
          .eq('id', folderId);
          
        if (error) throw error;
        
        // Update local state
        setFolders(folders.filter(folder => folder.id !== folderId));
        
        // If the deleted folder was selected, clear selection
        if (selectedFolderId === folderId && onFolderSelect) {
          onFolderSelect(null);
        }
        
        if (onFolderUpdated) {
          onFolderUpdated();
        }
      } catch (error) {
        console.error('Error deleting folder:', error);
        setError('Failed to delete folder');
      }
    }
  };

  const handleCancelEdit = () => {
    setEditingFolder(null);
    setError('');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#D59A4C]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {error && (
        <div className="text-red-600 text-sm bg-red-50 p-2 rounded mb-2">
          {error}
        </div>
      )}
      
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-medium text-gray-700">Folders</h3>
        <button
          onClick={() => setIsAddingFolder(true)}
          className="p-1 text-gray-600 hover:text-gray-900 transition-colors"
          title="Add folder"
        >
          <SafeIcon icon={FiFolderPlus} className="w-4 h-4" />
        </button>
      </div>
      
      {/* Add new folder input */}
      <AnimatePresence>
        {isAddingFolder && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="flex items-center space-x-2 mb-2"
          >
            <input
              type="text"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder="Folder name"
              className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-[#D59A4C] focus:border-transparent"
              autoFocus
            />
            <button
              onClick={handleAddFolder}
              className="p-1 text-green-600 hover:text-green-800"
              title="Save"
            >
              <SafeIcon icon={FiCheck} className="w-4 h-4" />
            </button>
            <button
              onClick={() => {
                setIsAddingFolder(false);
                setNewFolderName('');
                setError('');
              }}
              className="p-1 text-red-600 hover:text-red-800"
              title="Cancel"
            >
              <SafeIcon icon={FiX} className="w-4 h-4" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* All Searches option */}
      <div
        onClick={() => onFolderSelect(null)}
        className={`flex items-center justify-between p-2 rounded-md cursor-pointer ${
          !selectedFolderId ? 'bg-[#D59A4C20] text-[#D59A4C]' : 'hover:bg-gray-100'
        }`}
      >
        <div className="flex items-center">
          <SafeIcon icon={FiFolder} className="w-4 h-4 mr-2" />
          <span className="text-sm">All Searches</span>
        </div>
        <SafeIcon icon={FiChevronRight} className="w-4 h-4" />
      </div>
      
      {/* Folder list */}
      {folders.map((folder) => (
        <div
          key={folder.id}
          className={`flex items-center justify-between p-2 rounded-md ${
            selectedFolderId === folder.id ? 'bg-[#D59A4C20] text-[#D59A4C]' : 'hover:bg-gray-100'
          }`}
        >
          {editingFolder === folder.id ? (
            <div className="flex items-center w-full space-x-1">
              <input
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-[#D59A4C] focus:border-transparent"
                autoFocus
              />
              <button
                onClick={handleUpdateFolder}
                className="p-1 text-green-600 hover:text-green-800"
                title="Save"
              >
                <SafeIcon icon={FiCheck} className="w-3 h-3" />
              </button>
              <button
                onClick={handleCancelEdit}
                className="p-1 text-red-600 hover:text-red-800"
                title="Cancel"
              >
                <SafeIcon icon={FiX} className="w-3 h-3" />
              </button>
            </div>
          ) : (
            <>
              <div 
                className="flex items-center flex-1 cursor-pointer"
                onClick={() => onFolderSelect(folder.id)}
              >
                <SafeIcon icon={FiFolder} className="w-4 h-4 mr-2" />
                <span className="text-sm truncate">{folder.name}</span>
              </div>
              <div className="flex items-center">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditFolder(folder);
                  }}
                  className="p-1 text-gray-500 hover:text-gray-700"
                  title="Rename folder"
                >
                  <SafeIcon icon={FiEdit2} className="w-3 h-3" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteFolder(folder.id);
                  }}
                  className="p-1 text-gray-500 hover:text-red-600"
                  title="Delete folder"
                >
                  <SafeIcon icon={FiTrash2} className="w-3 h-3" />
                </button>
              </div>
            </>
          )}
        </div>
      ))}
      
      {folders.length === 0 && !isAddingFolder && (
        <p className="text-sm text-gray-500 italic p-2">
          No folders created yet
        </p>
      )}
    </div>
  );
};

export default FolderList;